import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { auditApi } from '../api/client'
import { RunDetailDrawer } from './RunDetailDrawer'
import { useExpandablePanel } from '../hooks/useExpandablePanel'
import { ExpandToggle } from './ExpandToggle'
import './WorkflowRunsFeed.css'

// 10 rows/page keeps the card bounded to ~one screen on mobile, where the
// previous 50/page meant the card grew vertically with every new run until it
// dwarfed its sibling card in the same dash-zone-grid--2 row.
const PAGE_SIZE = 10

export function WorkflowRunsFeed() {
  const [page, setPage] = useState(1)
  const [openRun, setOpenRun] = useState<string | null>(null)
  const q = useQuery({
    queryKey: ['workflow-runs', page],
    queryFn: () => auditApi.listRuns(PAGE_SIZE, page),
    refetchInterval: 10000,
  })
  const total = q.data?.total ?? 0
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const rows = q.data?.runs ?? []
  const { expanded, toggle, scrollClassName } = useExpandablePanel('workflow-runs')
  return (
    <div className="card runs-feed">
      <div className="runs-feed-head">
        <h3>Workflow runs</h3>
        <div className="runs-feed-head-right">
          <span className="runs-feed-total">{total} total</span>
          <ExpandToggle expanded={expanded} onToggle={toggle} />
        </div>
      </div>
      <div className={`${scrollClassName} runs-feed-table-scroll`}>
        <table className="runs-feed-table">
          <thead>
            <tr><th>Started</th><th>Mode</th><th>Result</th><th>Sig</th><th>Ord</th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.run_id} className="runs-feed-row"
                  role="button" tabIndex={0}
                  onClick={() => setOpenRun(r.run_id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      setOpenRun(r.run_id)
                    }
                  }}>
                <td>{r.started_at ? r.started_at.replace('T', ' ').slice(0, 19) + ' UTC' : '—'}</td>
                <td>{r.mode}</td>
                <td data-ok={r.success}>{r.success ? 'success' : 'failed'}</td>
                <td>{r.signals_generated}/{r.signals_passed_sanity}</td>
                <td>{r.orders_placed}</td>
              </tr>
            ))}
            {q.isLoading && (
              <tr><td colSpan={5} className="runs-feed-empty">Loading…</td></tr>
            )}
            {!q.isLoading && rows.length === 0 && (
              <tr><td colSpan={5} className="runs-feed-empty">No runs yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="runs-feed-pager">
        <button className="btn btn-sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</button>
        <span>{page} / {pages}</span>
        <button className="btn btn-sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</button>
      </div>
      <RunDetailDrawer runId={openRun} onClose={() => setOpenRun(null)} />
    </div>
  )
}
